/**
 * "Who can do this" (ADR-0019 §8), read forwards: pick a staff write action
 * and the server answers with everyone who may take it now, through which
 * roles, and with what limits. This file holds what the view shows, so the
 * component stays a table.
 *
 * Actions have no translated names yet (#185): they are shown by their id, in
 * mono, the way the audit log shows them today.
 */
import { ACTIONS, type ActionArea } from "@wallyo/contracts/actions";
import type { ListPermissionHoldersResponse, PermissionHolder } from "@wallyo/contracts/console";
import { t } from "../../i18n";
import { formatLimits } from "./team-format";

/** The staff write actions the select offers, grouped by the area they belong to. */
export type ActionGroup = { area: ActionArea; actions: string[] };

/** Every active staff write action, by area, in catalog order. */
export function staffWriteActions(): ActionGroup[] {
  const groups: ActionGroup[] = [];
  for (const action of ACTIONS) {
    if (action.actorType !== "staff") continue;
    if (action.effect !== "write") continue;
    if (action.status !== "active") continue;
    let group = groups.find((each) => each.area === action.area);
    if (!group) {
      group = { area: action.area, actions: [] };
      groups.push(group);
    }
    group.actions.push(action.actionId);
  }
  return groups;
}

export type WhoState =
  | { status: "loading"; data: ListPermissionHoldersResponse | null }
  | { status: "ready"; data: ListPermissionHoldersResponse }
  | {
      status: "failed";
      data: ListPermissionHoldersResponse | null;
      error: unknown;
    };

/** How a person holds it: the roles that grant it, or a value set for them. */
export function throughLabel(holder: PermissionHolder): string {
  const roles = holder.through.map((role) => role.name);
  // Both can be true: a value set for them, and roles that grant it too.
  if (holder.personal) return [t("team.who.personal"), ...roles].join(" · ");
  if (roles.length === 0) return "—";
  return roles.join(" · ");
}

/**
 * The most they may do alone: their limits, "no limit" when they have none,
 * and "—" when the permission carries none at all.
 */
export function limitLabel(holder: PermissionHolder, limited: boolean): string {
  if (!limited) return "—";
  return formatLimits(holder.limits);
}
